<template>
  <main v-if="auth.ready && auth.mustChangePassword && !route.meta.accountLink" class="password-gate">
    <section class="password-gate__card workspace-card">
      <p class="auth-kicker">Account security</p>
      <h1>Change your temporary password</h1>
      <p>Your temporary password can only be used to sign in. Choose a new password before opening the system.</p>
      <form @submit.prevent="changePassword">
        <label>Temporary password<input v-model="currentPassword" class="ui-input" type="password" autocomplete="current-password" required /></label>
        <label>New password (at least 12 characters)<input v-model="newPassword" class="ui-input" type="password" autocomplete="new-password" minlength="12" required /></label>
        <div v-if="passwordMessage" class="ui-notice" role="status">{{ passwordMessage }}</div>
        <button class="ui-button ui-button--primary" type="submit" :disabled="passwordSaving">{{ passwordSaving ? 'Saving...' : 'Save new password' }}</button>
      </form>
    </section>
  </main>
  <router-view v-else-if="auth.ready" />
  <main v-else class="app-loading" aria-busy="true" aria-live="polite">
    <section class="app-loading__panel ui-empty" aria-labelledby="app-loading-title">
      <div class="app-loading__marker" aria-hidden="true"></div>
      <h1 id="app-loading-title">Loading Writing System</h1>
      <p>Preparing your workspace...</p>
    </section>
  </main>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useAuthStore } from './stores/auth'
import { useRoute } from 'vue-router'

const auth = useAuthStore()
const route = useRoute()
const currentPassword = ref('')
const newPassword = ref('')
const passwordMessage = ref('')
const passwordSaving = ref(false)

onMounted(() => {
  auth.loadCurrentUser()
})

async function changePassword() {
  passwordSaving.value = true
  passwordMessage.value = ''
  try {
    await auth.changeTemporaryPassword(currentPassword.value, newPassword.value)
    currentPassword.value = ''
    newPassword.value = ''
  } catch (error) {
    passwordMessage.value = error.message || 'Could not change password.'
  } finally {
    passwordSaving.value = false
  }
}
</script>

<style>
.app-loading {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: var(--space-8) var(--space-6);
  background: var(--canvas);
}

.password-gate {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: var(--space-6);
  background: var(--canvas);
}

.password-gate__card {
  width: min(32rem, 100%);
  padding: var(--space-8);
  background: var(--surface);
}

.password-gate__card h1 {
  margin: 0 0 var(--space-3);
  font-family: var(--font-academic);
}

.password-gate__card form,
.password-gate__card label {
  display: grid;
  gap: var(--space-3);
}

.password-gate__card form {
  margin-top: var(--space-6);
}

.app-loading__panel {
  width: min(420px, 100%);
  min-height: 220px;
  border-style: solid;
  background: var(--surface);
}

.app-loading__marker {
  width: var(--space-8);
  height: 3px;
  background: var(--accent);
  animation: loading-marker 1.2s ease-in-out infinite alternate;
}

.app-loading h1 {
  margin: var(--space-3) 0 0;
  color: var(--ink);
  font-family: var(--font-academic);
  font-size: 1.5rem;
}

.app-loading p {
  margin: 0;
  color: var(--ink-muted);
}

@keyframes loading-marker {
  from {
    transform: scaleX(0.35);
  }

  to {
    transform: scaleX(1);
  }
}

@media (max-width: 767px) {
  .app-loading {
    padding: var(--space-4);
  }
}
</style>
